import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import {
  FieldTypes,
  SCALAR_FIELD_TYPE_OPTIONS,
  type FieldType,
} from '../../../schema/api';
import type {
  CandidateTableColumnRequest,
  CandidateTableColumnResponse,
} from '../api';

export interface CandidateFieldColumnsEditorProps {
  /** Columns currently persisted on the candidate field. */
  value: ReadonlyArray<CandidateTableColumnResponse> | null | undefined;
  /** Disables every interactive control while a save is in-flight. */
  busy: boolean;
  /**
   * Called when the user finishes editing the column list (blur / add / remove).
   * Receives the full replacement list — the backend overwrites verbatim.
   */
  onChange: (next: CandidateTableColumnRequest[]) => void;
}

function toRequest(col: CandidateTableColumnResponse): CandidateTableColumnRequest {
  const rawType = col.type as FieldType;
  // The wire type is the FieldType enum name; coerce to a scalar (drop "Table" if it somehow leaks).
  const type: FieldType =
    rawType === FieldTypes.Table ? FieldTypes.String : rawType;
  return {
    name: col.name,
    type,
    required: col.required,
    pattern: col.pattern ?? null,
    hint: col.hint ?? null,
  };
}

/**
 * Compact table editor for columns inside a Table candidate field. Mutations are batched
 * client-side: the editor mirrors the server-side list in a local draft and pushes the full
 * replacement to the parent on blur / add / remove (same pattern as the hint textarea).
 */
export function CandidateFieldColumnsEditor({
  value,
  busy,
  onChange,
}: CandidateFieldColumnsEditorProps): JSX.Element {
  const initial = useMemo<CandidateTableColumnRequest[]>(
    () => (value ?? []).map(toRequest),
    [value],
  );
  const [draft, setDraft] = useState<CandidateTableColumnRequest[]>(initial);

  // Reset the draft when the upstream value changes (e.g. another tab edited the field).
  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const persisted = useMemo(() => JSON.stringify(initial), [initial]);

  const commit = (next: CandidateTableColumnRequest[]): void => {
    setDraft(next);
    // Always emit; even an empty list is meaningful (clears all columns server-side).
    onChange(next);
  };

  const commitOnBlur = (): void => {
    if (JSON.stringify(draft) === persisted) {
      return;
    }
    onChange(draft);
  };

  const handleCellChange = <K extends keyof CandidateTableColumnRequest>(
    index: number,
    key: K,
    next: CandidateTableColumnRequest[K],
  ): void => {
    setDraft((prev) => {
      const copy = prev.slice();
      const current = copy[index];
      if (current === undefined) {
        return prev;
      }
      copy[index] = { ...current, [key]: next };
      return copy;
    });
  };

  const handleAdd = (): void => {
    commit([
      ...draft,
      {
        name: '',
        type: FieldTypes.String,
        required: false,
        pattern: null,
        hint: null,
      },
    ]);
  };

  const handleRemove = (index: number): void => {
    commit(draft.filter((_, i) => i !== index));
  };

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Columnas de la tabla
        </div>
        <span className="text-xs text-slate-500">{draft.length} columnas</span>
      </div>

      {draft.length === 0 ? (
        <div className="mt-2 rounded border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Una tabla necesita al menos una columna. Añade la primera para empezar.
        </div>
      ) : (
        <div className="mt-2 overflow-x-auto rounded border border-slate-200 bg-white">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-2 py-1 text-left font-semibold">Nombre</th>
                <th className="px-2 py-1 text-left font-semibold">Tipo</th>
                <th className="px-2 py-1 text-left font-semibold">Obligatorio</th>
                <th className="px-2 py-1 text-left font-semibold">Regex</th>
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {draft.map((col, index) => (
                <tr key={index} className="border-t border-slate-100">
                  <td className="px-2 py-1">
                    <input
                      type="text"
                      className="w-full rounded border border-slate-300 bg-white px-2 py-1 font-mono text-xs text-slate-800 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400 disabled:bg-slate-100"
                      value={col.name}
                      onChange={(e) => handleCellChange(index, 'name', e.target.value)}
                      onBlur={commitOnBlur}
                      disabled={busy}
                      placeholder="columna_snake"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <select
                      className="w-full rounded border border-slate-300 bg-white px-1 py-1 text-xs text-slate-800 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400 disabled:bg-slate-100"
                      value={col.type}
                      onChange={(e) => {
                        const next = e.target.value as FieldType;
                        const updated = draft.slice();
                        const current = updated[index];
                        if (current === undefined) {
                          return;
                        }
                        updated[index] = { ...current, type: next };
                        commit(updated);
                      }}
                      disabled={busy}
                    >
                      {SCALAR_FIELD_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1 text-center">
                    <input
                      type="checkbox"
                      checked={col.required}
                      onChange={(e) => {
                        const updated = draft.slice();
                        const current = updated[index];
                        if (current === undefined) {
                          return;
                        }
                        updated[index] = { ...current, required: e.target.checked };
                        commit(updated);
                      }}
                      disabled={busy}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="text"
                      className="w-full rounded border border-slate-300 bg-white px-2 py-1 font-mono text-xs text-slate-800 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400 disabled:bg-slate-100"
                      value={col.pattern ?? ''}
                      onChange={(e) =>
                        handleCellChange(index, 'pattern', e.target.value === '' ? null : e.target.value)
                      }
                      onBlur={commitOnBlur}
                      disabled={busy}
                      placeholder="opcional"
                    />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      className="text-rose-600 hover:text-rose-700 disabled:opacity-50"
                      onClick={() => handleRemove(index)}
                      disabled={busy}
                      aria-label={`Eliminar columna ${col.name || index + 1}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden strokeWidth={2} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button
        type="button"
        className="btn-secondary mt-2 inline-flex items-center gap-1 text-xs"
        onClick={handleAdd}
        disabled={busy}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden strokeWidth={2.25} />
        Añadir columna
      </button>
    </div>
  );
}
